import { axiosBaseQuery } from '../utils/rtk-query';
import { createApi } from '@reduxjs/toolkit/query/react';
const Config = require('Config');

interface PricingTier {
  monthly: number;
  yearly?: number;
}

export interface BinLookupResponse {
  country?: string;
  'country-code'?: string;
  'card-brand'?: string;
  'ip-city'?: string;
  'ip-blocklists'?: string[];
  'ip-country-code3'?: string;
  'is-commercial'?: boolean;
  'ip-country'?: string;
  'bin-number'?: string;
  issuer?: string;
  'issuer-website'?: string;
  'ip-region'?: string;
  valid?: boolean;
  'card-type'?: string;
  'is-prepaid'?: boolean;
  'ip-blocklisted'?: boolean;
  'card-category'?: string;
  'issuer-phone'?: string;
  'currency-code'?: string;
  'ip-matches-bin'?: boolean;
  'country-code3'?: string;
  'ip-country-code'?: string;
}
export interface BinLookup {
  cardNumber: string;
}
export interface PTSubscriptionPricing {
  yearlyDiscountPercent: number;
  premium: PricingTier;
  legacy: PricingTier;
  enterprise: PricingTier;
}
export type CreateSubscriptionResponse = {
  subscriptionId: string;
  profile: {
    customerProfileId: string;
    customerPaymentProfileId: string;
    customerAddressId: string;
  };
};
export interface CreatePTSubscriptionPayload {
  firstName: string;
  lastName: string;
  lastFour: string;
  subscriptionName: 'PT Premium' | 'PT Enterprise';
  paymentTerm: 'monthly' | 'yearly';
  paymentProfileId: string;
}

export interface CreatePTPaymentProfilePayload {
  cardData: string;
  cardPaymentType?: string;
  agreementOfPurchase: string;
  neutrinoData?: BinLookupResponse;
  addressInfo: {
    firstName: string;
    lastName: string;
    address: string;
    state: string;
    zip: string;
  };
}
export interface CreatePTSubscriptionFromFactoringFundsPayload {
  subscriptionName: 'PT Premium' | 'PT Enterprise';
  paymentTerm: 'monthly' | 'yearly';
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  company: string;
  device_info: {
    type: string;
    os: string;
    device_language: string;
    timezone: string;
  };
  purchase_acceptance: string;
  cost_acceptance: string;
}

interface CancelSubscriptionPayload {
  subscriptionId: string;
}

interface ContactUsPayload {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  subscriptionName: 'Legacy' | 'Enterprise';
}

interface PaymentInterval {
  length: number;
  unit: string;
}

interface PaymentSchedule {
  interval: PaymentInterval;
  startDate: string;
  totalOccurrences: number;
  trialOccurrences: number;
}

interface CreditCard {
  cardNumber: string;
  expirationDate: string;
}

interface Payment {
  creditCard: CreditCard;
}

interface BillTo {
  firstName: string;
  lastName: string;
  address: string;
  state: string;
  zip: string;
}

interface PaymentProfile {
  customerPaymentProfileId: string;
  payment: Payment;
  customerType: string;
  billTo: BillTo;
}

interface CustomerProfile {
  paymentProfile: PaymentProfile;
  customerProfileId: string;
  merchantCustomerId: string;
  email: string;
}

interface Subscription {
  name: string;
  paymentSchedule: PaymentSchedule;
  amount: number;
  trialAmount: number;
  status: string;
  profile: CustomerProfile;
}

interface PTSubscriptionResponse {
  subscription: Subscription;
  id: string;
  paymentProfileId: string;
}

interface AuthorizeDotNetCreditCard {
  cardNumber: string;
  expirationDate: string;
  cardType: string;
  issuerNumber: string;
}

interface AuthorizeDotNetPayment {
  creditCard: AuthorizeDotNetCreditCard;
}

interface AuthorizeDotNetBillTo {
  state: string;
  zip: string;
}

interface AuthorizeDotNetPaymentProfile {
  customerPaymentProfileId: string;
  payment: AuthorizeDotNetPayment;
  billTo: AuthorizeDotNetBillTo;
}

interface AuthorizeDotNetProfile {
  paymentProfiles?: AuthorizeDotNetPaymentProfile[];
  profileType: string;
  customerProfileId: string;
  merchantCustomerId: string;
  email: string;
}

interface AuthorizeDotNetMessageMessageInfo {
  code: string;
  text: string;
}

interface AuthorizeDotNetMessage {
  resultCode: string;
  message: AuthorizeDotNetMessageMessageInfo[];
}

interface CustomerProfileResponse {
  profile: AuthorizeDotNetProfile;
  messages: AuthorizeDotNetMessage[];
  cardPaymentType?: string;
  newPaymentProfile?: AuthorizeDotNetPaymentProfile;
}

interface CustomerPaymentProfileId {
  paymentProfileId: string;
  source?: string;
}

interface DeleteCustomerPaymentProfileResponse {
  messages: AuthorizeDotNetMessage;
}

const paymentServiceApi = createApi({
  reducerPath: 'paymentServiceApi',
  baseQuery: axiosBaseQuery({ baseUrl: Config.stcPaymentServiceUrl }),
  tagTypes: ['ptSubscription'],
  endpoints: (builder) => ({
    getPtSubscriptionPricing: builder.query<PTSubscriptionPricing, void>({
      query: () => ({
        url: `/pt/subscription/pricing`,
        method: 'GET',
      }),
    }),
    getPtSubscription: builder.query<PTSubscriptionResponse, undefined>({
      query: () => ({
        url: `/pt/subscription`,
        method: 'GET',
      }),
      providesTags: ['ptSubscription'],
    }),
    sendContactUsForm: builder.mutation<any, ContactUsPayload>({
      query: (payload) => ({
        url: `/pt/subscription/enterprise/contact`,
        method: 'POST',
        data: payload,
      }),
    }),
    creatSubscription: builder.mutation<CreateSubscriptionResponse, CreatePTSubscriptionPayload>({
      query: (data) => ({
        url: '/pt/subscription',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ptSubscription'],
    }),
    createSubscriptionFromFactoringFunds: builder.mutation<
      { message: string },
      CreatePTSubscriptionFromFactoringFundsPayload
    >({
      query: (data) => ({
        url: '/pt/subscription/factoring-funds',
        method: 'POST',
        data,
      }),
    }),
    cancelSubscription: builder.mutation<any, CancelSubscriptionPayload>({
      query: (data) => ({
        url: '/pt/subscription/cancel',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ptSubscription'],
    }),
    binLookup: builder.mutation<BinLookupResponse, BinLookup>({
      query: (data) => ({
        url: '/bin-lookup',
        method: 'POST',
        data,
      }),
    }),
    checkSubscriptionHistory: builder.query<boolean, void>({
      query: () => ({
        url: '/pt/subscription/history',
        method: 'GET',
      }),
      transformResponse: (response: { hasHistory: boolean }) => response.hasHistory,
    }),
    createPtPaymentProfile: builder.mutation<CustomerProfileResponse, CreatePTPaymentProfilePayload>({
      query: (data) => ({
        url: '/pt/profile',
        method: 'POST',
        data,
      }),
    }),
    deletePaymentProfile: builder.mutation<DeleteCustomerPaymentProfileResponse | string, CustomerPaymentProfileId>({
      query: (data) => ({
        url: '/pt/profile/payment',
        method: 'DELETE',
        data,
      }),
    }),
  }),
});

export const {
  useGetPtSubscriptionPricingQuery,
  useGetPtSubscriptionQuery,
  useBinLookupMutation,
  useCancelSubscriptionMutation,
  useCreatSubscriptionMutation,
  useCreateSubscriptionFromFactoringFundsMutation,
  useSendContactUsFormMutation,
  useCheckSubscriptionHistoryQuery,
  useCreatePtPaymentProfileMutation,
  useDeletePaymentProfileMutation,
} = paymentServiceApi;

export default paymentServiceApi;
